import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Loader from "../Loader";
import { getCertificates } from "../../api/requests";
import Head from "./Head";
import Body from "./Body";
import Footer from "./Footer";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

/* global BigInt */
const Certificates = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [emptyRows, setEmptyRows] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [referenceData, setReferenceData] = useState([]); // the reference data from which filtering and sorting is taken
    const [searched, setSearched] = useState("");
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("commonName");

    const [sortedData, setSortedData] = useState([]);

    const token = useSelector((state) => state.auth.token);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const organizeSerialToBigInt = (tempData) => {
        return tempData.map((cert) => {
            const copyCert = { ...cert };
            if (cert["serialNumber"]) {
                let bn;
                if (cert["serialNumber"].includes(":")) {
                    bn = BigInt(
                        "0x" + cert["serialNumber"].replaceAll(":", "")
                    );
                } else {
                    bn = BigInt(cert["serialNumber"]);
                }
                copyCert["serialNumber"] = bn;
            } else {
                copyCert["serialNumber"] = BigInt("0");
            }
            return copyCert;
        });
    };
    const avoidLayoutJump = (tempData) => {
        setEmptyRows(
            page > 0
                ? Math.max(0, (1 + page) * rowsPerPage - tempData.length)
                : 0
        );
    };
    useEffect(() => {
        async function fetchData() {
            let tempData = await getCertificates(token);
            tempData = organizeSerialToBigInt(tempData); // replace the certificate serial hexa number string to Big Int to support the sorting
            setData(tempData);
            setReferenceData(tempData);
            // Avoid a layout jump when reaching the last page with empty rows.
            setSortedData(stableSort(tempData, getComparator(order, orderBy)));
            avoidLayoutJump(tempData);
            setIsLoading(false);
        }
        if (token !== "") {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const resetPaging = () => {
        setPage(0);
        setEmptyRows(0);
    };
    const isFilteredOrSearched = (tempData) => {
        return !(tempData.length === 0);
    };
    let setPaging = (tempData) => {
        if (isFilteredOrSearched(tempData)) {
            resetPaging();
        } else {
            // Avoid a layout jump when reaching the last page with empty rows.
            avoidLayoutJump(tempData);
        }
    };
    const filterByStatus = () => {
        return referenceData.filter((row) =>
            selectedStatuses.includes(row.status)
        );
    };
    const applySearch = (tempData, searchedVal) => {
        let dataToSearch;
        dataToSearch = isFilteredOrSearched(tempData)
            ? tempData
            : [...referenceData];
        return dataToSearch.filter((row) => {
            return row.commonName
                .toLowerCase()
                .includes(searchedVal.toLowerCase());
        });
    };
    const filterAndSearch = (searchedVal = "") => {
        let tempData = [];
        if (selectedStatuses.length !== 0) {
            tempData = filterByStatus();
        }
        if (searchedVal !== "") {
            tempData = applySearch(tempData, searchedVal);
        }
        if (!isFilteredOrSearched(tempData)) {
            tempData = [...referenceData];
        }
        setData(tempData);
        setPaging(tempData);
    };

    useEffect(() => {
        filterAndSearch(searched);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatuses, searched]);

    useEffect(() => {
        setSortedData(stableSort(data, getComparator(order, orderBy)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, order, orderBy]);

    const requestSearch = (searchedVal) => {
        setSearched(searchedVal);
        filterAndSearch(searchedVal);
    };

    const cancelSearch = () => {
        requestSearch("");
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    function descendingComparator(a, b, orderBy) {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }

    function getComparator(order, orderBy) {
        return order === "desc"
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    // This method is created for cross-browser compatibility, if you don't
    // need to support IE11, you can use Array.prototype.sort() directly
    function stableSort(array, comparator) {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) {
                return order;
            }
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }

    return isLoading ? (
        <Loader />
    ) : (
        <>
            <div style={{ marginTop: "-35px" }}>
                <label htmlFor="search-input">Search Input</label>
                <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    style={{ width: "22%" }}
                    placeholder="Search By Common Name"
                    inputProps={{ "aria-label": "search common name" }}
                    value={searched}
                    onChange={(event) => requestSearch(event.target.value)}
                    id={"search-input"}
                />

                {searched !== "" && (
                    <IconButton
                        sx={{ p: "10px" }}
                        aria-label="cancel"
                        onClick={() => cancelSearch()}
                    >
                        <CancelIcon />
                    </IconButton>
                )}
                <IconButton sx={{ p: "10px" }} aria-label="search">
                    <SearchIcon />
                </IconButton>
            </div>
            <TableContainer component={Paper}>
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <Head
                        statusList={referenceData.reduce((acc, certData) => {
                            return acc.includes(certData.status)
                                ? acc
                                : acc.concat([certData.status]);
                        }, [])}
                        selectedStatuses={selectedStatuses}
                        setSelectedStatuses={setSelectedStatuses}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <Body
                        key={uuidv4()}
                        data={sortedData}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        emptyRows={emptyRows}
                    />
                    <Footer
                        data={sortedData}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Table>
            </TableContainer>
        </>
    );
};

export default Certificates;
